import { Modal, Row, Col, Alert, Button, Container, Form } from "react-bootstrap";
import PortalService from '../../../services/portal.service';
import { useContext, useEffect, useState } from "react";
import StateContext from '../../../context/State';
import { VerifiedClient, ClientPortalModalType } from '../../../types';
import React from 'react';
import { useAppSelector } from '../../../../../redux/hooks';
import { ReducerEnum, lddStateObject, setClientPortalModalType } from '../../../../../redux/reducer';
import IDV from './IDV';
import { runInnerFunction } from "../../../../../compatManager";
import { getDataReader, RPWDataReader } from "../../../../../livedataFuncs";
import { renderClientIDDetailsInNewWindow } from './IDVPrintTemplate';
import { renderDownloadPDF } from "./IDVDownloadTemplate";


export type IdentityVerificationTaskModalProps = {
    dispatch: (arg0: {
        type: ReducerEnum;
        payload: any;
        key?: string;
        extraPayload?: Record<string, string>;
    }) => void;
}

const IdentityVerificationTaskModal = (props: IdentityVerificationTaskModalProps) => {

    const { state } = useContext(StateContext);
    const lawyerService = new PortalService(state);
    const province = lawyerService.getProvince()

    const [verificationData, setVerificationData] = useState<VerifiedClient[]>([]);
    const show: boolean = useAppSelector((state) => state.ClientPortalState?.ModalType === ClientPortalModalType.ID_VERIFICATION);
    const livedataRootContainer = useAppSelector((state: lddStateObject) => state.currentNavigation.livedataRootContainer);
    const processor: RPWDataReader = getDataReader(livedataRootContainer?.dataRoot);
    // State for Send Reminder Email modal
    const [showReminderModal, setShowReminderModal] = useState(false);
    const [selectedClientIndexes, setSelectedClientIndexes] = useState<number[]>([]);
    const [reminderNote, setReminderNote] = useState<string>("");
    // Case clients (name + email), storing clients' name and email, used for sendIDV email modal
    type CaseClient = { name: string; email: string };
    const caseClients: CaseClient[] = (processor?.getClientEmail?.() || []) as CaseClient[];
    const hasCaseClients = caseClients.length > 0;

    // eslint-disable-next-line
    useEffect(() => { getIdentityVerificationData(); }, [show])
    const getIdentityVerificationData = async () => {
        try {
            if (!show) return;
            const myHeaders = new Headers();
            myHeaders.append("Content-Type", "application/json");
            const requestOptions = {
                method: 'POST',
                headers: myHeaders,
                body: JSON.stringify({
                    "Appid": state.appId,
                    "Product": lawyerService.getSessionAppCode()
                })
            };
            fetch(`/${process.env.REACT_APP_MOBILE_ENDPOINT}/api/getTableRecords?SessionID=${lawyerService.getSessionId()}&compat=1`, requestOptions)
                .then(response => response.text())
                .then(result => {
                    setVerificationData(parseGetTableRecords(result));
                })
                .catch(error => console.log('error', error));
        } catch (err) {
            console.log(err);
        }
    };

    const parseGetTableRecords = (result: string): VerifiedClient[] => {
        if (result.includes('Detailed Error - 404.0 - Not Found')) { return [] }

        const xmlData = result;
        const parser = new DOMParser();
        const xmlDoc = parser.parseFromString(JSON.parse(xmlData).result, "text/xml");
        const EntityList = xmlDoc.getElementsByTagName('Entity');
        const data: VerifiedClient[] = [];
        for (let i = 0; i < EntityList.length; i++) {
            data.push(parseTableRecord(EntityList[i]));
        }
        return data;
    };

    const parseTableRecord = (Entity: Element): VerifiedClient => {
        const clientInformation: VerifiedClient["clientInformation"] = {
            Name: "",
            Status: "",
            Timestamp: "",
            Type: "",
            id: "",
        };
        mappingAttribute(clientInformation, Entity);
        const clientIDImages: VerifiedClient["clientIDImages"] = {
            backImage: "",
            frontImage: "",
            verifyFaceFrame: "",
        };
        mappingAttribute(clientIDImages, Entity.childNodes[0] as Element);
        const clientIdFraudCheck: VerifiedClient["clientIdFraudCheck"] = {
            failedChecks: "",
            json: "",
            notPerformedChecks: "",
            passedChecks: "",
            score: "",
            totalChecks: "",
            type: "",
            certainty: "",
        };
        mappingAttribute(clientIdFraudCheck, Entity.childNodes[0].childNodes[0] as Element);
        
        if (Entity.hasAttribute("expiredCheck")) {
            clientIdFraudCheck.expiredCheck = Entity.getAttribute("expiredCheck") as 'PASS' | 'FAIL';
        }
        if (Entity.hasAttribute("screenCheck")) {
            clientIdFraudCheck.screenCheck = Entity.getAttribute("screenCheck") as 'PASS' | 'FAIL';
        }
        if (Entity.hasAttribute("photocopyCheck")) {
            clientIdFraudCheck.photocopyCheck = Entity.getAttribute("photocopyCheck") as 'PASS' | 'FAIL';
        }
        
        var fraudCheckJson = JSON.parse(clientIdFraudCheck.json);
        const FaceVerificationPassed = fraudCheckJson.FaceVerificationPassed;
        const skipFaceVerification = fraudCheckJson.skipFaceVerification;
        return { Entity: Entity.outerHTML, clientInformation, clientIDImages, clientIdFraudCheck, FaceVerificationPassed, skipFaceVerification};    
        function mappingAttribute(copyTo: VerifiedClient["clientIDImages" | "clientInformation" | "clientIdFraudCheck"], node: Element) {
            if (node && node.attributes) {
                Object.keys(copyTo).forEach((attributeName) => {
                    if (node.hasAttribute(attributeName)) {
                        copyTo[attributeName] = node.getAttribute(attributeName);
                    }
                })
            }
        }
    };

    let setPageDirtyOnClose = false;
    const setDirty = () => { setPageDirtyOnClose = true; }
    const CloseHandler = () => {
        props.dispatch(setClientPortalModalType(ClientPortalModalType.NONE));
        if (setPageDirtyOnClose) {
            runInnerFunction("SetPageDirty", null);
        }
    }

    const getFileNumber = () => {
        return processor.getFileNumber()
    }

    // Handlers for Send Reminder Email modal
    const handleSendReminderEmail = () => {
        if (!hasCaseClients) return;
        // default to all clients selected
        setSelectedClientIndexes(caseClients.map((_, idx) => idx));
        setReminderNote("");
        setShowReminderModal(true);
    };

    const handleCloseReminderModal = () => {
        setShowReminderModal(false);
        setReminderNote("");
        setSelectedClientIndexes([]);
    };

    const toggleClientSelection = (index: number) => {
        setSelectedClientIndexes(prev =>
            prev.includes(index)
                ? prev.filter(i => i !== index)
                : [...prev, index]
        );
    };

    const handleConfirmSendReminder = async () => {
        try {
        const selectedClients = selectedClientIndexes.map(i => caseClients[i]);
        if (selectedClients.length === 0) {
            // Already disabled in UI when no clients being selected
            alert("Please select at least one client.");
            return;
        }
        // Payload for backend: who to send email to and lawyer optional note
        const payload = {
            clients: selectedClients.map(c => ({
                name: c.name,
                email: c.email,
            })),
            noteText: reminderNote || "",
        };
        // Call backend service through PortalService
        const response = await lawyerService.sendIDVReminder( state.portalId, payload );

        if (response?.message === "error") {
            alert("Failed to send reminder email.");
            return;
        }

        alert("Reminder email(s) sent.");
        handleCloseReminderModal();
        } catch (e) {
            console.error("Failed to send IDV reminder:", e);
            alert("Failed to send reminder email.");
        }
    };

    return (
    <>
        {show &&
            <Modal
                show={show}
                onHide={CloseHandler}
                size="xl"
                aria-labelledby="contained-modal-title-vcenter"
                centered
            >
                <Modal.Header closeButton>
                    <Container className="gx-0">
                        <Row className="gx-0">
                            <Col>
                                <Modal.Title id="contained-modal-title-vcenter">
                                    Submitted Verifications
                                </Modal.Title>
                            </Col>
                            <Col className="align-right">
                                <Button variant = "outline-primary" title="Download Details" onClick={() => renderDownloadPDF(verificationData, province, getFileNumber())} style={{ marginRight: '5px' }} disabled={verificationData.length === 0} >
                                    <i className= "fa-regular fa-download"></i> Download 
                                </Button>
                                <Button title="Print Details" onClick={() => renderClientIDDetailsInNewWindow(verificationData, province, getFileNumber())} style={{ marginRight: '15px' }} disabled={verificationData.length === 0}>
                                    <i className="fa-regular fa-print"></i> Print
                                </Button>

                                <Button
                                    variant="outline-primary"
                                    title="Send Reminder Email"
                                    onClick={handleSendReminderEmail}
                                    disabled={!hasCaseClients}
                                >
                                    <i className="fa-regular fa-envelope"></i> Send Reminder Email
                                </Button>
                            </Col>
                        </Row>
                    </Container>
                </Modal.Header>

                <Modal.Body>
                    {
                        verificationData.length !== 0 ?
                            <>
                                {['AB','ON','NS','BC'].includes(province) && verificationData.every(client => client.skipFaceVerification) &&
                                    <Alert variant="warning">
                                        Client Face Verification has been Disabled for this File.
                                    </Alert>
                                }
                                {verificationData.map((verifiedClient: VerifiedClient, index) => {
                                    return (
                                        <IDV idv={verifiedClient} province={province} setDirty={setDirty} key={verifiedClient.clientInformation.Name + "-fragment-" + index} />
                                    )
                                })}
                            </>
                            :
                            <Alert>No Clients have used the ClientConnect App to identify themselves.</Alert>
                    }
                </Modal.Body>
            </Modal>
        }

        {/* Send IDV Reminder Email modal */}
        <Modal
            show={showReminderModal}
            onHide={handleCloseReminderModal}
            centered
        >
            <Modal.Header closeButton>
                <Modal.Title>Send Reminder Email</Modal.Title>
            </Modal.Header>

            <Modal.Body>
                {/* If there is more than one client, show selection list */}
                {caseClients.length > 1 && (
                    <>
                        <div className="mb-3">
                            <strong>Send ID Verification Reminder to:</strong>
                        </div>

                        <Form>
                            {caseClients.map((client, index) => (
                                <Form.Check
                                    key={client.email + index}
                                    type="checkbox"
                                    className="mb-3"
                                    checked={selectedClientIndexes.includes(index)}
                                    onChange={() => toggleClientSelection(index)}
                                    label={
                                        <div>
                                            <div>{client.name}</div>
                                            <div
                                                className="text-muted"
                                                style={{ fontSize: "0.9rem" }}
                                            >
                                                {client.email}
                                            </div>
                                        </div>
                                    }
                                />
                            ))}
                        </Form>
                    </>
                )}

                {/* Always show note area */}
                <Form.Group className={caseClients.length > 1 ? "mt-3" : ""}>
                    <Form.Label>Add Note to Request Email</Form.Label>
                    <Form.Control
                        as="textarea"
                        rows={4}
                        placeholder="Enter Note Text"
                        value={reminderNote}
                        onChange={(e) => setReminderNote(e.target.value)}
                    />
                </Form.Group>
            </Modal.Body>

            <Modal.Footer className="d-flex justify-content-between">
                <Button variant="link" onClick={handleCloseReminderModal}>
                    Cancel
                </Button>
                <Button onClick={handleConfirmSendReminder} disabled={ caseClients.length > 1 && selectedClientIndexes.length === 0 }>
                    Send Reminder Email
                </Button>
            </Modal.Footer>
        </Modal>
    </>
    );
}

export default IdentityVerificationTaskModal;
